/**
 * Dicts Contracts
 * Zod schemas for dictionary API responses.
 *
 * API Response Structure (from backend RAW payloads):
 * - Generic dict: {id, title}
 * - delivery_options: {id, title, titleForPanel}
 * - payment_types: {id, title, titleForPanel}
 * - order_statuses: {id, title}
 * - eval_blocks: {id, title, shortTitle}
 *
 * @module api/contracts/dicts
 */
import { z } from "zod"

// LLM:METADATA
// :hierarchy: [Domain:API | Contract | Dicts]
// :relates-to: validates: "admin.client.js", used-by: "normalizeDict", "useCommonDicts.js"
// :rationale: "Strict RAW contract for dict endpoints. PHP mysqli driver returns string for integer DB columns by default. This is natural backend behavior - type casting happens in normalizer layer. Required title field. Endpoint-specific optional fields (titleForPanel, shortTitle) preserved where domain-valid."
// :contract: pre: "backend RAW payload from /api/dict/*", post: "validated dict item structure", invariant: "id is number, title is required string"
// :decision_cache: "Made title required and removed optional name field [DICTS-CONTRACT-001]. Corrected id type to number to match backend payload [DICTS-CONTRACT-002]."
// :references: SPEC: "Wave A/Track 2 - Strict RAW Contracts", AGENTS: "Jest API Contract Rules (Mandatory)"
// LLM:END

/**
 * Dictionary Item Schema (strict RAW contract)
 *
 * Backend reality (PHP mysqli driver default behavior):
 * - id: string (DB integer mediumint, but PHP mysqli returns strings by default)
 * - title: string (required display text)
 * - label: string (optional, backend sometimes includes separate label field)
 * - titleForPanel: string|null (optional, used by delivery_options/payment_types)
 * - shortTitle: string|null (optional, used by eval_blocks)
 * - value: unknown (optional, stores arbitrary value)
 * - active: number|null (optional, 1/0/null for active status)
 *
 * Type casting note: Normalizer converts string IDs to numbers for frontend.
 * This split maintains "strict but correct" principle - RAW matches backend reality.
 *
 * Note: Backend sends 'title' field, NOT 'name'. The normalizer handles
 * legacy compatibility for consumers expecting 'name'.
 */
export const DictItemSchema = z.object({
  id: z.number(),
  title: z.string(),
  label: z.string().optional(), // Backend may include label in some responses
  titleForPanel: z.string().nullable().optional(),
  shortTitle: z.string().nullable().optional(),
  value: z.unknown().optional(),
  active: z.number().nullable().optional(),
})

// Dictionary Response (array of items)
export const DictResponseSchema = z.array(DictItemSchema)

// ============================================================================
// Endpoint-Specific Strict Schemas (Group 3 - Admin Dict Endpoints)
// ============================================================================

/**
 * Order Statuses Dictionary Response
 * GET /api/dict/order_statuses
 * Items: {id, title, value?, active?}
 */
export const DictOrderStatusesResponseSchema = DictResponseSchema

/**
 * Managers Dictionary Response
 * GET /api/dict/managers
 * Items: {id, title, value?, active?}
 */
export const DictManagersResponseSchema = DictResponseSchema

/**
 * Payment Types Dictionary Response
 * GET /api/dict/payment_types
 * Items: {id, title, titleForPanel?, value?, active?}
 */
export const DictPaymentTypesResponseSchema = DictResponseSchema

/**
 * Delivery Options Dictionary Response
 * GET /api/dict/delivery_options
 * Items: {id, title, titleForPanel?, value?, active?}
 */
export const DictDeliveryOptionsResponseSchema = DictResponseSchema

/**
 * Suppliers Dictionary Response
 * GET /api/dict/suppliers
 * Items: {id, title, value?, active?}
 */
export const DictSuppliersResponseSchema = DictResponseSchema

/**
 * Dynamic Dictionary by Name Response
 * GET /dict/:dictName
 * Generic dict lookup, shape varies by dict type
 */
export const DictByNameResponseSchema = DictResponseSchema

/**
 * Eval Blocks Dictionary Response
 * GET /dict/eval_blocks
 * Items: {id, title, shortTitle?}
 */
export const DictEvalBlocksResponseSchema = DictResponseSchema

// ============================================================================
// Request Schemas (Write Operations)
// ============================================================================

/**
 * Dictionary Create Request Schema
 * POST /api/dicts
 *
 * Frontend sends dict item data for creation.
 * Backend converts 'title' to 'm_item_title' field.
 */
export const DictCreateRequestSchema = z.object({
  title: z.string().min(1, "Title is required and must not be empty"),
  titleForPanel: z.string().nullable().optional(),
  shortTitle: z.string().nullable().optional(),
  value: z.unknown().optional(),
  active: z.number().nullable().optional(),
})

/**
 * Dictionary Update Request Schema
 * PUT /api/dicts/:id
 *
 * All fields optional for partial updates.
 * Used when modifying existing dict items.
 */
export const DictUpdateRequestSchema = z.object({
  title: z.string().min(1).optional(),
  titleForPanel: z.string().nullable().optional(),
  shortTitle: z.string().nullable().optional(),
  value: z.unknown().optional(),
  active: z.number().nullable().optional(),
})

/**
 * Dictionary Delete Response Schema
 * DELETE /api/dicts/:id
 *
 * Backend response confirming deletion
 */
export const DictDeleteResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
})

// Type exports
export const DictResponse = DictResponseSchema
